import { Briefcase, ChevronDown, ChevronUp, Trash2, HelpCircle, GripVertical, Plus } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  parseWorkExperienceContent,
  serializeWorkExperienceContent
  
} from '../utils/workExperienceFields';
import type {WorkExperienceEntry} from '../utils/workExperienceFields';

import MarkdownSectionEditor from './MarkdownSectionEditor';
import RichTextToolbar from './RichTextToolbar';

function generateId(): string {
  return `work-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

interface WorkExperienceCardProps {
  content: string;
  sectionTitle: string;
  onContentChange: (content: string) => void;
  onDeleteSection?: () => void;
}

const WorkExperienceCard: React.FC<WorkExperienceCardProps> = ({
  content,
  sectionTitle,
  onContentChange,
  onDeleteSection,
}) => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<WorkExperienceEntry[]>(() => parseWorkExperienceContent(content));
  const [expandedId, setExpandedId] = useState<string | null>(() => {
    const initial = parseWorkExperienceContent(content);
    return initial[0]?.id ?? null;
  });

  useEffect(() => {
    const parsed = parseWorkExperienceContent(content);
    setEntries(parsed);
    setExpandedId((prev) => (parsed.some((e) => e.id === prev) ? prev : parsed[0]?.id ?? null));
  }, [content]);

  const persist = useCallback(
    (next: WorkExperienceEntry[]) => {
      setEntries(next);
      onContentChange(serializeWorkExperienceContent(next));
    },
    [onContentChange]
  );

  const updateEntry = useCallback(
    (id: string, updates: Partial<WorkExperienceEntry>) => {
      const next = entries.map((e) => (e.id === id ? { ...e, ...updates } : e));
      persist(next);
    },
    [entries, persist]
  );

  const addEntry = useCallback(() => {
    const newEntry: WorkExperienceEntry = { id: generateId() };
    const next = [...entries, newEntry];
    setEntries(next);
    setExpandedId(newEntry.id);
    onContentChange(serializeWorkExperienceContent(next));
  }, [entries, onContentChange]);

  const removeEntry = useCallback(
    (id: string) => {
      const next = entries.filter((e) => e.id !== id);
      setExpandedId(next[0]?.id ?? null);
      persist(next);
    },
    [entries, persist]
  );

  const toggleEntry = (id: string) => setExpandedId((prev) => (prev === id ? null : id));

  return (
    <div className="flex flex-col min-h-0">
      <div className="flex items-center justify-end gap-1 pb-2 border-b border-gray-100 dark:border-gray-600/60 flex-shrink-0">
        <div className="flex items-center gap-1 shrink-0">
          <span className="text-gray-400 dark:text-gray-500 p-1" title={t('optimizer.reorder', 'Reorder')}>
            <GripVertical className="w-4 h-4" />
          </span>
          <a href="#" onClick={(e) => e.preventDefault()} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1" title={t('optimizer.help', 'Help')} aria-label={t('optimizer.help', 'Help')}>
            <HelpCircle className="w-4 h-4" />
          </a>
          {onDeleteSection && (
            <button type="button" onClick={onDeleteSection} className="text-gray-400 hover:text-red-500 p-1" aria-label={t('optimizer.remove', 'Remove section')}>
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="py-2 space-y-2 flex-shrink-0">
        {entries.map((entry) => {
          const isExpanded = expandedId === entry.id;
          const dateRange = entry.fromDate
            ? `${entry.fromDate}${entry.currentlyHere ? ' – Present' : entry.toDate ? ` – ${entry.toDate}` : ''}`
            : null;
          const title =
            entry.jobTitle && entry.company
              ? `${entry.jobTitle} · ${entry.company}${dateRange ? `, ${dateRange}` : ''}`
              : entry.jobTitle || entry.company || `${t('optimizer.workExperience.entry', 'Work Experience')} #${entries.indexOf(entry) + 1}`;
          return (
            <div key={entry.id} className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/80 overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2">
                <button type="button" onClick={() => toggleEntry(entry.id)} className="flex-1 flex items-center gap-2 text-left min-w-0">
                  <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{title}</span>
                  {isExpanded ? <ChevronUp className="w-4 h-4 shrink-0 text-gray-500" /> : <ChevronDown className="w-4 h-4 shrink-0 text-gray-500" />}
                </button>
                <button type="button" onClick={() => removeEntry(entry.id)} className="text-gray-400 hover:text-red-500 p-1 shrink-0" aria-label={t('optimizer.remove', 'Remove')}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              {isExpanded && (
                <div className="px-3 pb-3 pt-0 space-y-3 border-t border-gray-100 dark:border-gray-600/60">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">{t('optimizer.workExperience.company', 'Company')}</label>
                      <input type="text" value={entry.company ?? ''} onChange={(e) => updateEntry(entry.id, { company: e.target.value })} className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800" placeholder="e.g. NETCRACKER" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">{t('optimizer.workExperience.jobTitle', 'Job title')}</label>
                      <input type="text" value={entry.jobTitle ?? ''} onChange={(e) => updateEntry(entry.id, { jobTitle: e.target.value })} className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800" placeholder="e.g. Sr. Technical Manager" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">{t('optimizer.workExperience.city', 'City')}</label>
                      <input type="text" value={entry.city ?? ''} onChange={(e) => updateEntry(entry.id, { city: e.target.value })} className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800" placeholder={t('optimizer.education.cityPlaceholder', 'City')} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">{t('optimizer.workExperience.country', 'Country')}</label>
                      <input type="text" value={entry.country ?? ''} onChange={(e) => updateEntry(entry.id, { country: e.target.value })} className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800" placeholder={t('optimizer.education.countryPlaceholder', 'Country')} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">{t('optimizer.workExperience.from', 'From')}</label>
                      <input type="text" value={entry.fromDate ?? ''} onChange={(e) => updateEntry(entry.id, { fromDate: e.target.value })} className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800" placeholder="04/2019" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">{t('optimizer.workExperience.to', 'To')}</label>
                      <input type="text" value={entry.toDate ?? ''} onChange={(e) => updateEntry(entry.id, { toDate: e.target.value })} className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800" placeholder="10/2023" />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!entry.currentlyHere} onChange={(e) => updateEntry(entry.id, { currentlyHere: e.target.checked })} className="rounded border-gray-300 dark:border-gray-600 text-[#6C47FF] focus:ring-[#6C47FF]" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('optimizer.workExperience.currentlyWorkHere', 'I currently work here')}</span>
                  </label>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5">{t('optimizer.workExperience.description', 'Description')}</label>
                    <div className="rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-800">
                      <RichTextToolbar textareaId={`work-desc-${entry.id}`} value={entry.description ?? ''} onChange={(v) => updateEntry(entry.id, { description: v })} />
                      <MarkdownSectionEditor id={`work-desc-${entry.id}`} value={entry.description ?? ''} onChange={(v) => updateEntry(entry.id, { description: v })} minHeight="96px" className="focus:ring-2 focus:ring-[#6C47FF] border-0" placeholder={t('optimizer.workExperience.descriptionPlaceholder', 'Responsibilities and achievements...')} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        <button type="button" onClick={addEntry} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-[#6C47FF]/40 hover:text-[#6C47FF] dark:hover:text-[#5B8DFF] hover:bg-[#6C47FF]/5 text-sm font-medium transition-colors">
          <Plus className="w-4 h-4" />
          {t('optimizer.workExperience.addWorkExperience', 'Add Work Experience')}
        </button>
      </div>
    </div>
  );
};

export default WorkExperienceCard;
